import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  models as modelsApi,
  projects as projectsApi,
  tracks as tracksApi,
} from "../../lib/api";
import { Button } from "../shared/Button";
import { IconButton } from "../shared/IconButton";
import { ModelPicker } from "../shared/ModelPicker";
import { X } from "../shared/Icon";
import { cn } from "../../lib/cn";
import { formatCost } from "../../lib/format";
import type { AutonomyLevel, Track, TrackPlan } from "../../lib/types";
import { AUTONOMY_LEVELS } from "../../lib/types";

interface Props {
  projectId: string;
  /** When set, the editor opens in EDIT mode pre-filled from this track. */
  track?: Track;
  onCancel: () => void;
  onCreated: (track: Track) => void;
}

interface EditableFeature {
  title: string;
  prompt: string;
}

interface EditableMilestone {
  title: string;
  features: EditableFeature[];
  acceptance: string[];
}

const emptyMilestone = (): EditableMilestone => ({
  title: "",
  features: [{ title: "", prompt: "" }],
  acceptance: [""],
});

const FIELD_LABEL = "block text-small text-text-secondary mb-1";
const FIELD_INPUT =
  "w-full bg-bg-inset border border-border-subtle rounded-md px-3 py-2 text-body text-text-primary placeholder:text-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent";

export function TrackEditor({ projectId, track, onCancel, onCreated }: Props) {
  const isEdit = !!track;
  // After a track materializes, editing the plan now syncs task cards: new
  // features become new cards, removed pending features have their cards
  // retired, and edited prompts land on still-pending cards. Cards that have
  // already started or landed are left alone — their history is load-bearing.
  // We surface that as an info note rather than the old "won't sync" warning.
  const planLive = isEdit && track!.status !== "planning";
  const [title, setTitle] = useState(track?.title ?? "");
  const [description, setDescription] = useState(track?.description ?? "");
  const [autonomy, setAutonomy] = useState<AutonomyLevel>(
    track?.autonomy_level ?? "agent",
  );
  const [workerModel, setWorkerModel] = useState(track?.models?.worker ?? "");
  const [validatorModel, setValidatorModel] = useState(
    track?.models?.validator ?? "",
  );
  const [milestones, setMilestones] = useState<EditableMilestone[]>(() =>
    track?.plan?.milestones?.length
      ? track.plan.milestones.map((m) => ({
          title: m.title ?? "",
          features: (m.features ?? []).map((f) => ({
            title: f.title ?? "",
            prompt: f.prompt ?? "",
          })),
          acceptance: m.acceptance?.length ? [...m.acceptance] : [""],
        }))
      : [emptyMilestone()],
  );
  const [parallel, setParallel] = useState(track?.parallel ?? false);
  const [sharedSession, setSharedSession] = useState(
    track?.shared_session ?? true,
  );
  const [estimate, setEstimate] = useState<string | null>(null);

  const modelCatalog = useQuery({
    queryKey: ["models"],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });
  const project = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => projectsApi.get(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });
  const models = modelCatalog.data?.models ?? [];
  const projectCli = project.data?.cli_binary;

  const plan = useMemo<TrackPlan>(
    () => ({
      milestones: milestones.map((m) => ({
        title: m.title,
        features: m.features
          .filter((f) => f.title.trim() || f.prompt.trim())
          .map((f, i) => ({
            title: f.title || `Feature ${i + 1}`,
            prompt: f.prompt,
            ordinal: i,
          })),
        acceptance: m.acceptance.filter((a) => a.trim()),
      })),
    }),
    [milestones],
  );

  // Estimate when the plan changes (debounced lightly).
  useEffect(() => {
    const handle = window.setTimeout(async () => {
      if (plan.milestones.every((m) => m.features.length === 0)) {
        setEstimate(null);
        return;
      }
      try {
        const est = await tracksApi.estimate({
          title: title || "Untitled",
          project_id: projectId,
          plan,
          models: {},
          autonomy_level: autonomy,
        });
        setEstimate(formatCost(est.cost_usd));
      } catch {
        setEstimate(null);
      }
    }, 350);
    return () => window.clearTimeout(handle);
  }, [plan, title, projectId, autonomy]);

  const createMutation = useMutation({
    mutationFn: () =>
      tracksApi.create({
        project_id: projectId,
        title: title || "Untitled track",
        description: description || undefined,
        plan,
        models: {
          ...(workerModel ? { worker: workerModel } : {}),
          ...(validatorModel ? { validator: validatorModel } : {}),
        },
        autonomy_level: autonomy,
        status: "planning",
        parallel,
        shared_session: sharedSession,
      }),
    onSuccess: (track) => onCreated(track),
  });

  const dispatchMutation = useMutation({
    mutationFn: async () => {
      const t = await tracksApi.create({
        project_id: projectId,
        title: title || "Untitled track",
        description: description || undefined,
        plan,
        models: {
          ...(workerModel ? { worker: workerModel } : {}),
          ...(validatorModel ? { validator: validatorModel } : {}),
        },
        autonomy_level: autonomy,
        status: "planning",
        parallel,
        shared_session: sharedSession,
      });
      await tracksApi.dispatch(t.id);
      return t;
    },
    onSuccess: (track) => onCreated(track),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      // PATCH replaces the whole models map, so start from the track's existing
      // roles (preserving any beyond worker/validator) and layer the two the
      // form exposes — clearing a field removes that override.
      const models: Record<string, string> = { ...(track?.models ?? {}) };
      if (workerModel) models.worker = workerModel;
      else delete models.worker;
      if (validatorModel) models.validator = validatorModel;
      else delete models.validator;
      return tracksApi.update(track!.id, {
        title: title || "Untitled track",
        description: description || undefined,
        plan,
        models,
        autonomy_level: autonomy,
        parallel,
        shared_session: sharedSession,
      });
    },
    onSuccess: (updated) => onCreated(updated),
  });

  const updateMilestone = (idx: number, fn: (m: EditableMilestone) => EditableMilestone) => {
    setMilestones((ms) => ms.map((m, i) => (i === idx ? fn(m) : m)));
  };

  return (
    <div className="space-y-4">
      {planLive && (
        <div className="rounded-md border border-border-subtle bg-bg-inset px-3 py-2 text-small text-text-secondary">
          This track has already started. Saving syncs the task cards to match
          the plan — new features become new cards, removed features have their
          pending cards retired, and prompt edits land on still-pending cards.
          Cards already started or finished are left alone.
        </div>
      )}
      <div>
        <label className={FIELD_LABEL} htmlFor="track-title">
          Title
        </label>
        <input
          id="track-title"
          className={FIELD_INPUT}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Auth flow rewrite"
        />
      </div>
      <div>
        <label className={FIELD_LABEL} htmlFor="track-desc">
          Description
        </label>
        <textarea
          id="track-desc"
          className={cn(FIELD_INPUT, "min-h-[60px] resize-y")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional. What is this track trying to achieve?"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={FIELD_LABEL}>Autonomy</label>
          <select
            className={FIELD_INPUT}
            value={autonomy}
            onChange={(e) => setAutonomy(e.target.value as AutonomyLevel)}
          >
            {AUTONOMY_LEVELS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-small uppercase tracking-wide text-text-tertiary">
          Role models
          <span className="ml-2 normal-case tracking-normal text-text-quaternary">
            the track's pick overrides each task's own model
          </span>
        </div>
        <div>
          <label className={FIELD_LABEL}>Worker</label>
          <div className="grid grid-cols-3 gap-2">
            <ModelPicker
              value={workerModel}
              onChange={setWorkerModel}
              models={models}
              defaultProvider={projectCli}
              modelPlaceholder="cheaper worker default"
            />
          </div>
        </div>
        <div>
          <label className={FIELD_LABEL}>Validator</label>
          <div className="grid grid-cols-3 gap-2">
            <ModelPicker
              value={validatorModel}
              onChange={setValidatorModel}
              models={models}
              defaultProvider={projectCli}
              modelPlaceholder="project default"
            />
          </div>
        </div>
      </div>

      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-accent cursor-pointer"
          checked={parallel}
          onChange={(e) => setParallel(e.target.checked)}
        />
        <span className="text-body text-text-primary">
          Run milestone features in parallel
          <span className="block text-small text-text-tertiary">
            Each feature dispatches in its own worktree at the same time. The
            validator runs against a joined branch once all features land. Leave
            off for sequential execution.
          </span>
        </span>
      </label>

      {!parallel && (
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-accent cursor-pointer"
            checked={sharedSession}
            onChange={(e) => setSharedSession(e.target.checked)}
          />
          <span className="text-body text-text-primary">
            Run the whole track in one session
            <span className="block text-small text-text-tertiary">
              All tasks run sequentially in a single agent session and branch, so
              the start-up context loads once instead of per task — cheaper on
              tokens, and you review &amp; confirm the track once. Turn off to give
              each task its own fresh session.
            </span>
          </span>
        </label>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-small uppercase tracking-wide text-text-tertiary">
            Milestones
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setMilestones((ms) => [...ms, emptyMilestone()])}
          >
            Add milestone
          </Button>
        </div>
        {milestones.map((m, idx) => (
          <fieldset
            key={idx}
            className="border border-border-subtle rounded-md p-3 space-y-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-mono text-text-quaternary">M{idx + 1}</span>
              <input
                className={cn(FIELD_INPUT, "flex-1")}
                value={m.title}
                onChange={(e) =>
                  updateMilestone(idx, (mm) => ({ ...mm, title: e.target.value }))
                }
                placeholder="Milestone title"
              />
              {milestones.length > 1 && (
                <IconButton
                  icon={<X />}
                  label="Remove milestone"
                  onClick={() =>
                    setMilestones((ms) => ms.filter((_, i) => i !== idx))
                  }
                />
              )}
            </div>

            <div className="space-y-2">
              {m.features.map((f, fIdx) => (
                <div
                  key={fIdx}
                  className="bg-bg-canvas border border-border-subtle rounded-md p-2 space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-mono text-text-quaternary">
                      F{fIdx + 1}
                    </span>
                    <input
                      className={cn(FIELD_INPUT, "flex-1")}
                      value={f.title}
                      onChange={(e) =>
                        updateMilestone(idx, (mm) => ({
                          ...mm,
                          features: mm.features.map((ff, j) =>
                            j === fIdx ? { ...ff, title: e.target.value } : ff,
                          ),
                        }))
                      }
                      placeholder="Feature title"
                    />
                    {m.features.length > 1 && (
                      <IconButton
                        icon={<X />}
                        label="Remove feature"
                        onClick={() =>
                          updateMilestone(idx, (mm) => ({
                            ...mm,
                            features: mm.features.filter((_, j) => j !== fIdx),
                          }))
                        }
                      />
                    )}
                  </div>
                  <textarea
                    className={cn(FIELD_INPUT, "min-h-[60px] resize-y")}
                    value={f.prompt}
                    onChange={(e) =>
                      updateMilestone(idx, (mm) => ({
                        ...mm,
                        features: mm.features.map((ff, j) =>
                          j === fIdx ? { ...ff, prompt: e.target.value } : ff,
                        ),
                      }))
                    }
                    placeholder="Detailed prompt for the worker"
                  />
                </div>
              ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  updateMilestone(idx, (mm) => ({
                    ...mm,
                    features: [...mm.features, { title: "", prompt: "" }],
                  }))
                }
              >
                Add feature
              </Button>
            </div>

            <div>
              <label className={FIELD_LABEL}>Acceptance criteria</label>
              {m.acceptance.map((a, aIdx) => (
                <div className="flex items-center gap-2 mb-1" key={aIdx}>
                  <input
                    className={cn(FIELD_INPUT, "flex-1")}
                    value={a}
                    onChange={(e) =>
                      updateMilestone(idx, (mm) => ({
                        ...mm,
                        acceptance: mm.acceptance.map((aa, j) =>
                          j === aIdx ? e.target.value : aa,
                        ),
                      }))
                    }
                    placeholder="e.g. login form passes a11y audit"
                  />
                  {m.acceptance.length > 1 && (
                    <IconButton
                      icon={<X />}
                      label="Remove criterion"
                      onClick={() =>
                        updateMilestone(idx, (mm) => ({
                          ...mm,
                          acceptance: mm.acceptance.filter((_, j) => j !== aIdx),
                        }))
                      }
                    />
                  )}
                </div>
              ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  updateMilestone(idx, (mm) => ({
                    ...mm,
                    acceptance: [...mm.acceptance, ""],
                  }))
                }
              >
                Add criterion
              </Button>
            </div>
          </fieldset>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border-subtle">
        <div className="text-small text-text-tertiary">
          Estimated cost: <span className="text-text-secondary">{estimate ?? "—"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          {isEdit ? (
            <Button
              variant="primary"
              disabled={updateMutation.isPending || !title}
              onClick={() => updateMutation.mutate()}
            >
              Save changes
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                disabled={createMutation.isPending || !title}
                onClick={() => createMutation.mutate()}
              >
                Save plan
              </Button>
              <Button
                variant="primary"
                disabled={dispatchMutation.isPending || !title}
                onClick={() => dispatchMutation.mutate()}
              >
                Save & dispatch
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
